/**
 * The Edit-with-AI stream — `POST /skills/{id}/regenerate` against the local
 * agent. Ported from `bimgo-ai-web`'s `skillRegenerateService.ts`, rebuilt on
 * this app's own reader (`streamAsk`): the endpoint speaks `/ask`'s NDJSON
 * protocol by design, so the skill editor renders with the same components as
 * the main chat and the two surfaces cannot drift apart.
 *
 * What this module adds over the raw reader is the one event the editor must
 * *react* to rather than merely display: **`commit_skill_version`'s
 * `tool_end`**. Its output carries `{ version, message, path, summary }` — a
 * new version was just persisted on disk — and the controller refetches the
 * working copy off it. The output arrives in whatever wrapper LangChain chose
 * (a plain string, a ToolMessage, a content array), so `parseCommitResult`
 * walks the known shapes; the reference's walker, kept because every shape in
 * it was met in production.
 */
import type { Attachment } from "./attachments";
import { streamAsk, type AskCallbacks, type ResumeDecision } from "./chat";
import type { AgentClient } from "./agentClient";
import { skillPath } from "./skills";
import { parseSkillPatch, patchOutcome, type SkillPatch } from "./skillPatch";

export interface CommitSkillVersionResult {
  version: number;
  message?: string;
  path?: string;
  /** The agent's one-line change description — the toast's second line. */
  summary?: string;
}

/** The commit tool's result, dug out of whatever wrapper it arrived in. */
export function parseCommitResult(output: unknown): CommitSkillVersionResult | null {
  if (output == null) return null;
  if (typeof output === "string") {
    try {
      return parseCommitResult(JSON.parse(output));
    } catch {
      return null;
    }
  }
  if (typeof output !== "object") return null;
  const o = output as Record<string, unknown>;
  // LangChain ToolMessage: { lc: 1, kwargs: { content } } or { content }.
  if (o["lc"] === 1 && o["kwargs"]) return parseCommitResult(o["kwargs"]);
  if (typeof o["content"] === "string") return parseCommitResult(o["content"]);
  if (Array.isArray(o["content"])) {
    const text = (o["content"] as unknown[]).find(
      (c): c is { type: string; text: string } =>
        !!c && typeof c === "object" && (c as { type?: unknown }).type === "text" &&
        typeof (c as { text?: unknown }).text === "string",
    );
    if (text) return parseCommitResult(text.text);
  }
  if (typeof o["version"] === "number") {
    return {
      version: o["version"],
      ...(typeof o["message"] === "string" ? { message: o["message"] } : {}),
      ...(typeof o["path"] === "string" ? { path: o["path"] } : {}),
      ...(typeof o["summary"] === "string" ? { summary: o["summary"] } : {}),
    };
  }
  return null;
}

export interface RegenerateParams {
  skillId: string;
  organizationId: string;
  userId: string;
  projectId: string | null;
  accessToken: string;
  /** The matching connected instance — the agent refuses without one (409). */
  targetSessionId: string;
  /** Initial turn only. */
  prompt?: string;
  /** Resume turn only — the same grammar as `/ask`. */
  resume?: ResumeDecision;
  /** Abandon the persisted conversation and start fresh. Ignored on resume. */
  newThread?: boolean;
  /**
   * The conversation this turn continues — `null` starts a new one. Set for an
   * AutoCAD skill, whose turns always name their thread and go through the
   * agent's workspace gate (docs/proposals/workspaces.md §4); absent for a Revit
   * skill, which still continues the pointer stored on the skill.
   */
  threadId?: string | null;
  /**
   * An AutoCAD cross-version build (docs/proposals/workspaces.md): the turn makes the
   * skill work on other AutoCAD years, started from a year it supports; the agent asks
   * which. Sent on the build's resume too.
   */
  crossVersion?: boolean;
  attachments?: Attachment[];
  agentMode?: string;
  llmProvider?: string;
  llmModel?: string;
  /** The composer's reasoning chip (#111), shared with the chat pane. */
  reasoningEffort?: string;
  abortSignal?: AbortSignal;
}

export interface RegenerateCallbacks extends AskCallbacks {
  /**
   * A new version was just persisted on disk. The code text is deliberately
   * not carried here — on some HITL resume paths the tool's input never
   * streams, so the working copy on disk is the only honest source and the
   * caller refetches it.
   */
  onCodeCommitted?: (result: CommitSkillVersionResult) => void;
}

/** One Edit-with-AI turn. Resolves when the stream ends, as `ask` does. */
export async function regenerateSkill(
  params: RegenerateParams,
  callbacks: RegenerateCallbacks,
  deps: { client?: AgentClient } = {},
): Promise<void> {
  const body: Record<string, unknown> = {
    organization_id: params.organizationId,
    user_id: params.userId,
    ...(params.projectId ? { project_id: params.projectId } : {}),
    target_session_id: params.targetSessionId,
    ...(params.agentMode ? { agent_mode: params.agentMode } : {}),
    ...(params.llmProvider && params.llmModel
      ? { llm_provider: params.llmProvider, llm_model: params.llmModel }
      : {}),
    ...(params.reasoningEffort ? { reasoning_effort: params.reasoningEffort } : {}),
  };
  if (params.threadId !== undefined) body["thread_id"] = params.threadId;
  if (params.crossVersion) body["cross_version"] = true;
  if (params.resume) {
    body["resume"] = params.resume;
  } else {
    body["prompt"] = params.prompt ?? "";
    if (params.newThread && params.threadId === undefined) body["new_thread"] = true;
    if (params.attachments && params.attachments.length > 0) body["attachments"] = params.attachments;
  }

  const patches = new Map<string, SkillPatch>();
  return streamAsk(
    {
      path: skillPath(params.skillId, "/regenerate"),
      body,
      accessToken: params.accessToken,
      abortSignal: params.abortSignal,
      organizationId: params.organizationId,
    },
    {
      ...callbacks,
      onSteps: (steps) => callbacks.onSteps(steps.map((step) => {
        const candidatePatch = step.id ? patches.get(step.id) : undefined;
        return candidatePatch ? { ...step, candidatePatch } : step;
      })),
      onRawEvent: (data) => {
        if (data["tool"] === "stage_skill_candidate" && typeof data["id"] === "string") {
          const id = data["id"];
          if (data["type"] === "tool_start") {
            const patch = parseSkillPatch(data["input"]);
            if (patch) patches.set(id, patch);
          } else if (data["type"] === "tool_end") {
            const patch = patches.get(id);
            if (patch) patches.set(id, { ...patch, ...patchOutcome(data["output"]) });
          }
        }
        callbacks.onRawEvent?.(data);
        // A cross-version build also saves a version — of every year it wrote.
        if (data["type"] === "tool_end" && (data["tool"] === "commit_skill_version" || data["tool"] === "build_autocad_versions")) {
          const result = parseCommitResult(data["output"]);
          if (result) callbacks.onCodeCommitted?.(result);
        }
      },
    },
    deps,
  );
}
